import { z } from 'zod';

/**
 * PTSTDM enumerations (Phase 5, PLAN §3.3 sec. 1 "Enumerations", D-012): the arrays are the source
 * of truth for the database CHECK constraints (`packages/db/src/schema/ptstdm-*.ts`), the Zod
 * contracts (`contracts/ptstdm-*.ts`) and the clients. The values are exactly the column values of
 * the data model, so REST, realtime and webhook payloads share one vocabulary (sec. 1.1).
 * `@orbit/domain/ptstdm/types.ts` declares the same unions for the pure code;
 * `packages/domain/src/ptstdm/enums-sync.test.ts` keeps them in step.
 */

const schemaOf = <T extends readonly [string, ...string[]]>(values: T) => z.enum(values);

// ---------------------------------------------------------------- calendar and periods (sec. 4)

/**
 * `holidays.kind` (the tasks section's table, extended in Phase 5). Phase 4 defines the same list;
 * the merge keeps one definition (D-251).
 */
export const HOLIDAY_KINDS = ['full', 'half_day'] as const;
export type HolidayKind = (typeof HOLIDAY_KINDS)[number];
export const holidayKindSchema = schemaOf(HOLIDAY_KINDS);

export const LEAVE_DAY_PORTIONS = ['full', 'first_half', 'second_half'] as const;
export type LeaveDayPortion = (typeof LEAVE_DAY_PORTIONS)[number];
export const leaveDayPortionSchema = schemaOf(LEAVE_DAY_PORTIONS);

export const LEAVE_TYPES = [
  'casual',
  'sick',
  'earned',
  'comp_off',
  'optional_holiday',
  'unpaid',
  'maternity',
  'paternity',
  'other',
] as const;
export type LeaveType = (typeof LEAVE_TYPES)[number];
export const leaveTypeSchema = schemaOf(LEAVE_TYPES);

export const LEAVE_STATUSES = ['active', 'cancelled'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];
export const leaveStatusSchema = schemaOf(LEAVE_STATUSES);

export const LEAVE_SOURCES = ['manual', 'hrms'] as const;
export type LeaveSource = (typeof LEAVE_SOURCES)[number];
export const leaveSourceSchema = schemaOf(LEAVE_SOURCES);

/** `kpi_periods.period_type` (financial-year quarters, halves and years, D-063). */
export const KPI_PERIOD_TYPES = ['month', 'quarter', 'half_year', 'year'] as const;
export type KpiPeriodType = (typeof KPI_PERIOD_TYPES)[number];
export const kpiPeriodTypeSchema = schemaOf(KPI_PERIOD_TYPES);

/** `kpi_periods.status`, forward only. */
export const KPI_PERIOD_STATUSES = ['open', 'frozen', 'closed'] as const;
export type KpiPeriodStatusValue = (typeof KPI_PERIOD_STATUSES)[number];
export const kpiPeriodStatusSchema = schemaOf(KPI_PERIOD_STATUSES);

// ---------------------------------------------------------------- positions (sec. 5)

export const POSITION_LEVELS = ['manager', 'doer'] as const;
export type PositionLevel = (typeof POSITION_LEVELS)[number];
export const positionLevelSchema = schemaOf(POSITION_LEVELS);

export const POSITION_STATUSES = ['draft', 'active', 'archived'] as const;
export type PositionStatus = (typeof POSITION_STATUSES)[number];
export const positionStatusSchema = schemaOf(POSITION_STATUSES);

/** Computed by `position_seat_status(ws, date)`, never stored. */
export const SEAT_STATUSES = ['vacant', 'partially_filled', 'filled', 'overfilled'] as const;
export type SeatStatus = (typeof SEAT_STATUSES)[number];
export const seatStatusSchema = schemaOf(SEAT_STATUSES);

export const ASSIGNMENT_TYPES = ['regular', 'acting', 'cover'] as const;
export type PositionAssignmentType = (typeof ASSIGNMENT_TYPES)[number];
export const assignmentTypeSchema = schemaOf(ASSIGNMENT_TYPES);

/** A cover takes only critical items by default (SPEC §7.11). */
export const COVER_SCOPES = ['critical_only', 'all_items'] as const;
export type CoverScope = (typeof COVER_SCOPES)[number];
export const coverScopeSchema = schemaOf(COVER_SCOPES);

export const ASSIGNMENT_END_REASONS = [
  'resigned',
  'terminated',
  'transferred',
  'promoted',
  'cover_ended',
  'restructure',
  'other',
] as const;
export type AssignmentEndReason = (typeof ASSIGNMENT_END_REASONS)[number];
export const assignmentEndReasonSchema = schemaOf(ASSIGNMENT_END_REASONS);

/** `ramp_up_plans.status`, `kras.status`, `kpi_definitions.status`, `skills.status`, … */
export const ACTIVE_ARCHIVED = ['active', 'archived'] as const;
export type ActiveArchived = (typeof ACTIVE_ARCHIVED)[number];
export const activeArchivedSchema = schemaOf(ACTIVE_ARCHIVED);

// ---------------------------------------------------------------- KRAs and recurring tasks (sec. 6)

/** `recurring_task_definitions.evidence_type` and `checklist_instances.evidence_type`. */
export const EVIDENCE_TYPES = [
  'none',
  'checkbox',
  'number',
  'photo',
  'file',
  'link',
  'comment',
] as const;
export type ChecklistEvidenceType = (typeof EVIDENCE_TYPES)[number];
export const evidenceTypeSchema = schemaOf(EVIDENCE_TYPES);

/** `checklist_evidence.evidence_type`: every evidence type except `none`. */
export const EVIDENCE_RECORD_TYPES = [
  'checkbox',
  'number',
  'photo',
  'file',
  'link',
  'comment',
] as const;
export type EvidenceRecordType = (typeof EVIDENCE_RECORD_TYPES)[number];

/** `recurring_task_definitions.checker_type` (maker-checker). */
export const DEFINITION_CHECKER_TYPES = ['none', 'manager', 'position'] as const;
export type DefinitionCheckerType = (typeof DEFINITION_CHECKER_TYPES)[number];
export const definitionCheckerTypeSchema = schemaOf(DEFINITION_CHECKER_TYPES);

/** `checklist_instances.checker_type`; ad-hoc items may also be checked by the run's starter. */
export const CHECKER_TYPES = ['none', 'manager', 'position', 'starter'] as const;
export type ChecklistCheckerType = (typeof CHECKER_TYPES)[number];
export const checkerTypeSchema = schemaOf(CHECKER_TYPES);

export const CRITICALITIES = ['critical', 'normal'] as const;
export type ChecklistCriticality = (typeof CRITICALITIES)[number];
export const criticalitySchema = schemaOf(CRITICALITIES);

/** What happens to a duty when its holder is on leave (critical → reassign, normal → skip). */
export const LEAVE_RULES = ['skip', 'reassign_to_cover'] as const;
export type ChecklistLeaveRule = (typeof LEAVE_RULES)[number];
export const leaveRuleSchema = schemaOf(LEAVE_RULES);

/** `recurring_task_definitions.escalate_to` ("missed by X → notify …"). */
export const ESCALATION_TARGETS = ['manager', 'checker', 'position'] as const;
export type EscalationTarget = (typeof ESCALATION_TARGETS)[number];
export const escalationTargetSchema = schemaOf(ESCALATION_TARGETS);

export const RECURRING_TASK_STATUSES = ['draft', 'active', 'paused', 'archived'] as const;
export type RecurringTaskStatus = (typeof RECURRING_TASK_STATUSES)[number];
export const recurringTaskStatusSchema = schemaOf(RECURRING_TASK_STATUSES);

/** `trigger_event_types.source`. */
export const TRIGGER_EVENT_SOURCES = ['system', 'app', 'manual'] as const;
export type TriggerEventSource = (typeof TRIGGER_EVENT_SOURCES)[number];
export const triggerEventSourceSchema = schemaOf(TRIGGER_EVENT_SOURCES);

// ---------------------------------------------------------------- checklists (sec. 6.5–6.10)

export const GENERATION_MODES = [
  'scheduled',
  'catch_up',
  'assignment_change',
  'leave_change',
  'holiday_change',
  'trigger',
  'manual',
] as const;
export type ChecklistGenerationMode = (typeof GENERATION_MODES)[number];

export const GENERATION_RUN_STATUSES = ['running', 'succeeded', 'failed'] as const;
export type GenerationRunStatus = (typeof GENERATION_RUN_STATUSES)[number];

export const CHECKLIST_SOURCES = ['recurring', 'adhoc'] as const;
export type ChecklistSource = (typeof CHECKLIST_SOURCES)[number];
export const checklistSourceSchema = schemaOf(CHECKLIST_SOURCES);

export const CHECKLIST_STATUSES = [
  'pending',
  'done',
  'partial',
  'not_done',
  'missed',
  'skipped',
  'cancelled',
] as const;
export type ChecklistInstanceStatus = (typeof CHECKLIST_STATUSES)[number];
export const checklistStatusSchema = schemaOf(CHECKLIST_STATUSES);

/** What the assignee may set (`POST /checklist-items/:id/complete`). */
export const CHECKLIST_OUTCOMES = ['done', 'partial', 'not_done'] as const;
export type ChecklistOutcome = (typeof CHECKLIST_OUTCOMES)[number];
export const checklistOutcomeSchema = schemaOf(CHECKLIST_OUTCOMES);

/**
 * `checklist_instances.skip_reason`. `holiday` extends the sec. 6.6 list: a holiday added later
 * removes an occurrence whose rule is `skip` (D-170).
 */
export const CHECKLIST_SKIP_REASONS = [
  'leave',
  'vacant',
  'assignment_ended',
  'generation_outage',
  'definition_archived',
  'run_cancelled',
  'holiday',
] as const;
export type ChecklistSkipReason = (typeof CHECKLIST_SKIP_REASONS)[number];
export const checklistSkipReasonSchema = schemaOf(CHECKLIST_SKIP_REASONS);

export const ASSIGNMENT_REASONS = [
  'holder',
  'cover',
  'default_cover_position',
  'manager_fallback',
  'vacancy_fallback',
  'adhoc',
  'reassigned',
] as const;
export type ChecklistAssignmentReason = (typeof ASSIGNMENT_REASONS)[number];
export const assignmentReasonSchema = schemaOf(ASSIGNMENT_REASONS);

/** `checklist_instances.check_status` (maker-checker). */
export const CHECK_STATUSES = ['not_required', 'pending', 'approved', 'rejected'] as const;
export type ChecklistCheckStatus = (typeof CHECK_STATUSES)[number];
export const checkStatusSchema = schemaOf(CHECK_STATUSES);

/** `checklist_verifications.decision`. */
export const CHECK_DECISIONS = ['approved', 'rejected'] as const;
export type CheckDecision = (typeof CHECK_DECISIONS)[number];
export const checkDecisionSchema = schemaOf(CHECK_DECISIONS);

export const CHECKLIST_TEMPLATE_STATUSES = ['draft', 'active', 'archived'] as const;
export type ChecklistTemplateStatus = (typeof CHECKLIST_TEMPLATE_STATUSES)[number];
export const checklistTemplateStatusSchema = schemaOf(CHECKLIST_TEMPLATE_STATUSES);

export const CHECKLIST_RUN_STATUSES = ['active', 'completed', 'cancelled'] as const;
export type ChecklistRunStatus = (typeof CHECKLIST_RUN_STATUSES)[number];
export const checklistRunStatusSchema = schemaOf(CHECKLIST_RUN_STATUSES);

export const CHECKLIST_RUN_SOURCES = ['manual', 'position_onboarding', 'trigger_event'] as const;
export type ChecklistRunSource = (typeof CHECKLIST_RUN_SOURCES)[number];
export const checklistRunSourceSchema = schemaOf(CHECKLIST_RUN_SOURCES);

/** `checklist_template_items.assignee_rule.type`. */
export const ASSIGNEE_RULE_TYPES = [
  'starter',
  'position',
  'user',
  'run_param',
  'subject_manager',
] as const;
export type AssigneeRuleType = (typeof ASSIGNEE_RULE_TYPES)[number];
export const assigneeRuleTypeSchema = schemaOf(ASSIGNEE_RULE_TYPES);

// ---------------------------------------------------------------- KPIs and targets (sec. 7)

export const KPI_UNITS = ['count', 'inr', 'pct', 'minutes', 'hours', 'score'] as const;
export type KpiUnitValue = (typeof KPI_UNITS)[number];
export const kpiUnitSchema = schemaOf(KPI_UNITS);

export const KPI_DIRECTIONS = ['higher_better', 'lower_better'] as const;
export type KpiDirectionValue = (typeof KPI_DIRECTIONS)[number];
export const kpiDirectionSchema = schemaOf(KPI_DIRECTIONS);

export const KPI_AGGREGATIONS = ['sum', 'avg', 'latest', 'min', 'max', 'ratio'] as const;
export type KpiAggregationValue = (typeof KPI_AGGREGATIONS)[number];
export const kpiAggregationSchema = schemaOf(KPI_AGGREGATIONS);

/** Only `daily` KPIs appear on the agenda and report. */
/**
 * D-1194: `quarterly` joins the three. It is descriptive — the only rule that reads this is
 * "a manager-rated KPI is rated monthly" — and the agenda and evening report still show only
 * `daily` ones, so a quarterly KPI behaves exactly like a weekly or monthly one everywhere.
 * Aggregation is driven by `aggregation` (sum/avg/latest/ratio), not by this.
 */
export const KPI_MEASUREMENT_FREQUENCIES = ['daily', 'weekly', 'monthly', 'quarterly'] as const;
export type KpiMeasurementFrequency = (typeof KPI_MEASUREMENT_FREQUENCIES)[number];
export const kpiMeasurementFrequencySchema = schemaOf(KPI_MEASUREMENT_FREQUENCIES);

export const KPI_SOURCE_TYPES = ['system', 'derived', 'manager_rated', 'self_reported'] as const;
export type KpiSourceType = (typeof KPI_SOURCE_TYPES)[number];
export const kpiSourceTypeSchema = schemaOf(KPI_SOURCE_TYPES);

/** Counters computed from app data (`kpi-rollup`); ratios are built from pairs of them. */
export const KPI_DERIVED_METRICS = [
  'checklist_due_count',
  'checklist_done_credit',
  'checklist_critical_miss_count',
  'tasks_due_count',
  'tasks_done_on_time_count',
  'say_do_committed_count',
  'say_do_on_time_count',
  'report_expected_count',
  'report_on_time_count',
  'agenda_expected_count',
  'agenda_on_time_count',
  'training_mandatory_due_count',
  'training_mandatory_on_time_count',
  'meeting_actions_due_count',
  'meeting_actions_closed_count',
] as const;
export type KpiDerivedMetric = (typeof KPI_DERIVED_METRICS)[number];
export const kpiDerivedMetricSchema = schemaOf(KPI_DERIVED_METRICS);

/**
 * Derived metrics whose inputs arrive with later phases: tasks (Phase 4, merged in parallel; the
 * KPI editor still asks the tasks port whether its feed is available). Training and meetings
 * (Phase 7) landed with wave 4, so their counters are no longer listed and the import wizard no
 * longer warns about them.
 */
export const KPI_DERIVED_METRICS_LATER: Readonly<Record<string, 4 | 7>> = Object.freeze({
  tasks_due_count: 4,
  tasks_done_on_time_count: 4,
  say_do_committed_count: 4,
  say_do_on_time_count: 4,
});

export const SCORECARD_STATUSES = ['draft', 'active'] as const;
export type ScorecardStatus = (typeof SCORECARD_STATUSES)[number];
export const scorecardStatusSchema = schemaOf(SCORECARD_STATUSES);

/** `kpi_targets.period_type` and `position_scorecard_kpis.target_period_type`. */
export const TARGET_PERIOD_TYPES = ['month', 'quarter'] as const;
export type TargetPeriod = (typeof TARGET_PERIOD_TYPES)[number];
export const targetPeriodTypeSchema = schemaOf(TARGET_PERIOD_TYPES);

/** `kpi_targets.approval_status`, `integration_user_links.approval_status` (Phase 8, D-099/D-100). */
export const APPROVAL_STATUSES = ['not_required', 'pending', 'approved', 'rejected'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];
export const approvalStatusSchema = schemaOf(APPROVAL_STATUSES);

/** `kpi_entries.source`, highest priority first (D-064). */
export const KPI_ENTRY_SOURCES = [
  'override',
  'system',
  'derived',
  'manager_rated',
  'self_reported',
] as const;
export type KpiEntrySource = (typeof KPI_ENTRY_SOURCES)[number];
export const kpiEntrySourceSchema = schemaOf(KPI_ENTRY_SOURCES);

export const KPI_INGEST_KINDS = ['webhook', 'poll', 'manual_upload'] as const;
export type KpiIngestKind = (typeof KPI_INGEST_KINDS)[number];

export const KPI_VERIFICATION_STATUSES = ['unverified', 'verified', 'rejected'] as const;
export type KpiVerificationStatus = (typeof KPI_VERIFICATION_STATUSES)[number];
export const kpiVerificationStatusSchema = schemaOf(KPI_VERIFICATION_STATUSES);

/** `POST /kpi-entries/:id/verify` decisions. */
export const KPI_VERIFY_DECISIONS = ['verified', 'rejected'] as const;
export type KpiVerifyDecision = (typeof KPI_VERIFY_DECISIONS)[number];
export const kpiVerifyDecisionSchema = schemaOf(KPI_VERIFY_DECISIONS);

export const KPI_REVISION_KINDS = [
  'create',
  'edit',
  'reingest',
  'verify',
  'reject',
  'correct',
  'approve_override',
] as const;
export type KpiRevisionKind = (typeof KPI_REVISION_KINDS)[number];

export const DISCREPANCY_STATUSES = ['open', 'accepted', 'rejected'] as const;
export type DiscrepancyStatus = (typeof DISCREPANCY_STATUSES)[number];
export const discrepancyStatusSchema = schemaOf(DISCREPANCY_STATUSES);

/** Why an entry is in the verification queue (`KpiEntryDto.pendingReason`). */
export const KPI_PENDING_REASONS = ['required_for_pay', 'spot_check', 'variance'] as const;
export type KpiPendingReason = (typeof KPI_PENDING_REASONS)[number];
export const kpiPendingReasonSchema = schemaOf(KPI_PENDING_REASONS);

/** SPEC §7 principle 3: 3–6 KPIs per position recommended, warn above 6, block above 10. */
export const KPI_COUNT_LIMITS = Object.freeze({
  recommendedMin: 3,
  recommendedMax: 6,
  hardMax: 10,
});

/** Weights of one scorecard version sum to exactly this (two decimals). */
export const SCORECARD_WEIGHT_TOTAL = 100;

// ---------------------------------------------------------------- integrations (sec. 8)

export const INTEGRATION_ADAPTER_KINDS = [
  'crm',
  'telephony',
  'billing',
  'cocloud',
  'comsky',
  'support_desk',
  'uptime',
  'generic_webhook',
  'csv',
  'hrms',
  'plaud',
] as const;
export type IntegrationAdapterKind = (typeof INTEGRATION_ADAPTER_KINDS)[number];
export const integrationAdapterKindSchema = schemaOf(INTEGRATION_ADAPTER_KINDS);

export const INTEGRATION_DELIVERIES = ['webhook', 'poll', 'upload'] as const;
export type IntegrationDelivery = (typeof INTEGRATION_DELIVERIES)[number];
export const integrationDeliverySchema = schemaOf(INTEGRATION_DELIVERIES);

export const INTEGRATION_SOURCE_STATUSES = ['active', 'paused', 'error', 'archived'] as const;
export type IntegrationSourceStatus = (typeof INTEGRATION_SOURCE_STATUSES)[number];
export const integrationSourceStatusSchema = schemaOf(INTEGRATION_SOURCE_STATUSES);

export const SOURCE_MAPPING_TARGET_KINDS = ['kpi', 'trigger_event'] as const;
export type SourceMappingTargetKind = (typeof SOURCE_MAPPING_TARGET_KINDS)[number];
export const sourceMappingTargetKindSchema = schemaOf(SOURCE_MAPPING_TARGET_KINDS);

export const VALUE_TRANSFORMS = ['as_is', 'count_one', 'rupees_to_paise'] as const;
export type ValueTransform = (typeof VALUE_TRANSFORMS)[number];
export const valueTransformSchema = schemaOf(VALUE_TRANSFORMS);

export const SOURCE_MAPPING_STATUSES = ['active', 'paused', 'pending_approval'] as const;
export type SourceMappingStatus = (typeof SOURCE_MAPPING_STATUSES)[number];
export const sourceMappingStatusSchema = schemaOf(SOURCE_MAPPING_STATUSES);

export const INGESTION_RUN_KINDS = ['webhook', 'poll', 'upload', 'backfill'] as const;
export type IngestionRunKind = (typeof INGESTION_RUN_KINDS)[number];

export const INGESTION_STATUSES = ['running', 'succeeded', 'partial', 'failed'] as const;
export type IngestionStatus = (typeof INGESTION_STATUSES)[number];

/** `SourceRecord.mode` / `integration_events.mode`: add to the day's total, or replace it. */
export const SOURCE_RECORD_MODES = ['increment', 'absolute'] as const;
export type SourceRecordMode = (typeof SOURCE_RECORD_MODES)[number];
export const sourceRecordModeSchema = schemaOf(SOURCE_RECORD_MODES);

export const INTEGRATION_EVENT_STATUSES = ['accepted', 'unmapped', 'rejected', 'void'] as const;
export type IntegrationEventStatus = (typeof INTEGRATION_EVENT_STATUSES)[number];

/** Per-record rejection codes of `POST /integrations/kpi/:sourceId/events`. */
export const INGEST_REJECTION_CODES = [
  'unknown_user',
  'unknown_metric',
  'invalid_value',
  'period_locked',
] as const;
export type IngestRejectionCode = (typeof INGEST_REJECTION_CODES)[number];

// ---------------------------------------------------------------- daily tracking (sec. 9)

export const DAILY_STATUSES = ['draft', 'submitted', 'late', 'missed', 'exempt'] as const;
export type DailyTrackingStatus = (typeof DAILY_STATUSES)[number];
export const dailyStatusSchema = schemaOf(DAILY_STATUSES);

/** DTO status: `not_started` = no row yet. */
export const DAILY_DTO_STATUSES = ['not_started', ...DAILY_STATUSES] as const;
export type DailyDtoStatus = (typeof DAILY_DTO_STATUSES)[number];
export const dailyDtoStatusSchema = schemaOf(DAILY_DTO_STATUSES);

export const DAILY_EXEMPT_REASONS = [
  'leave',
  'holiday',
  'non_working_day',
  'no_position',
  'system_outage',
] as const;
export type DailyExemptReason = (typeof DAILY_EXEMPT_REASONS)[number];
export const dailyExemptReasonSchema = schemaOf(DAILY_EXEMPT_REASONS);

export const LATE_SUBMISSION_UNTIL = ['window_end', 'next_agenda_window_end'] as const;
export type LateSubmissionUntil = (typeof LATE_SUBMISSION_UNTIL)[number];
export const lateSubmissionUntilSchema = schemaOf(LATE_SUBMISSION_UNTIL);

export const TARGET_CHANGE_KINDS = ['none', 'raised', 'lowered'] as const;
export type AgendaTargetChangeKind = (typeof TARGET_CHANGE_KINDS)[number];

/** `daily_report_kpi_actuals.effective_source`: an entry source, or `none`. */
export const REPORT_VALUE_SOURCES = [...KPI_ENTRY_SOURCES, 'none'] as const;
export type ReportValueSource = (typeof REPORT_VALUE_SOURCES)[number];
export const reportValueSourceSchema = schemaOf(REPORT_VALUE_SOURCES);

/** A blocker that "needs action" creates a task or a decision request (tasks section). */
export const BLOCKER_ACTION_KINDS = ['task', 'decision_request'] as const;
export type BlockerActionKind = (typeof BLOCKER_ACTION_KINDS)[number];
export const blockerActionKindSchema = schemaOf(BLOCKER_ACTION_KINDS);

/**
 * Colour band of an attainment on the daily channel card and the manager daily view (D-255): `met`
 * at 100 % or more, `near` from the department's `achievementThresholdPct` (default 70), `below`
 * under it, `none` without a plan. `attainmentTone()` in contracts/ptstdm-daily.ts computes it.
 */
export const ATTAINMENT_TONES = ['met', 'near', 'below', 'none'] as const;
export type AttainmentTone = (typeof ATTAINMENT_TONES)[number];
export const attainmentToneSchema = schemaOf(ATTAINMENT_TONES);

/** Which daily card a `messages.metadata` holds (subtypes `agenda_card` / `report_card`). */
export const DAILY_CARD_KINDS = ['daily_channel', 'daily_prompt'] as const;
export type DailyCardKind = (typeof DAILY_CARD_KINDS)[number];

/** `POST /daily/report/:date/submit` `taskUpdates[].action` (applied through the task-feed port). */
export const REPORT_TASK_ACTIONS = ['start', 'block', 'complete'] as const;
export type ReportTaskAction = (typeof REPORT_TASK_ACTIONS)[number];
export const reportTaskActionSchema = schemaOf(REPORT_TASK_ACTIONS);

// ---------------------------------------------------------------- setup and import (sec. 10)

export const PTSTDM_IMPORT_ENTITIES = [
  'positions',
  'kras',
  'recurring_tasks',
  'kpi_definitions',
  'kpi_targets',
  'skills',
  'position_skill_requirements',
  'training_modules',
  'position_assignments',
] as const;
export type PtstdmImportEntity = (typeof PTSTDM_IMPORT_ENTITIES)[number];
export const ptstdmImportEntitySchema = schemaOf(PTSTDM_IMPORT_ENTITIES);

export const PTSTDM_IMPORT_STATUSES = [
  'uploaded',
  'mapped',
  'dry_run_running',
  'dry_run_ready',
  'committing',
  'committed',
  'failed',
  'cancelled',
] as const;
export type PtstdmImportStatus = (typeof PTSTDM_IMPORT_STATUSES)[number];
export const ptstdmImportStatusSchema = schemaOf(PTSTDM_IMPORT_STATUSES);

export const IMPORT_ROW_ACTIONS = ['create', 'update', 'skip', 'error'] as const;
export type ImportRowAction = (typeof IMPORT_ROW_ACTIONS)[number];
export const importRowActionSchema = schemaOf(IMPORT_ROW_ACTIONS);

/** Column transforms of the import mapping (`columnMap.<field>.transform`). */
export const IMPORT_COLUMN_TRANSFORMS = ['split_comma', 'trim', 'upper', 'date_dmy'] as const;
export type ImportColumnTransform = (typeof IMPORT_COLUMN_TRANSFORMS)[number];
export const importColumnTransformSchema = schemaOf(IMPORT_COLUMN_TRANSFORMS);

// ---------------------------------------------------------------- skill and training library (sec. 11, D-091)

/** 1 Aware, 2 Working, 3 Proficient, 4 Expert. */
export const SKILL_LEVELS = [1, 2, 3, 4] as const;

export const TRAINING_ASSESSMENT_TYPES = ['mcq', 'practical_signoff', 'none'] as const;
export type TrainingAssessmentType = (typeof TRAINING_ASSESSMENT_TYPES)[number];
export const trainingAssessmentTypeSchema = schemaOf(TRAINING_ASSESSMENT_TYPES);

export const TRAINING_MODULE_STATUSES = ['draft', 'published', 'archived'] as const;
export type TrainingModuleStatus = (typeof TRAINING_MODULE_STATUSES)[number];
export const trainingModuleStatusSchema = schemaOf(TRAINING_MODULE_STATUSES);
